/** @jsxImportSource theme-ui */

import React, { useState, Fragment } from "react"
import PropTypes from "prop-types"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faQuestionCircle, faCheckSquare } from '@fortawesome/free-solid-svg-icons'
import { faSquare } from '@fortawesome/free-regular-svg-icons'

import styled from "@emotion/styled"
import { Text, Box } from "theme-ui"
import ReactTooltip from "@huner2/react-tooltip"

import { randomString } from "../../util"
const propTypes = {
  label: PropTypes.string,
  labelPlacement: PropTypes.string,
  onChange: PropTypes.func,
  className: PropTypes.string,
  darkMode: PropTypes.bool,
  value: PropTypes.bool,
  disabled: PropTypes.bool
}

const defaultProps = {
  label: "",
  labelPlacement: "right",
  onChange: () => {},
  className: "",
  darkMode: false,
  value: false,
  disabled: false,
}

const Container = styled.div`
  display: flex;
  align-items: center;

  span {
    color: ${(props) => {
    if (props.darkMode) {
      if (props.disabled) {
        return props.theme.gray4
      }
      return "rgba(255, 255, 255, 0.87)"
    }

    if (props.disabled) {
      return props.theme.gray2
    }
    return props.theme.gray7
  }}
  }
`

const CheckboxIcon = styled(FontAwesomeIcon)`
  cursor: pointer;
  margin-right: ${props => (props.labelplacement === "right" && props.label ? "8px" : 0)};
  margin-left: ${props => (props.labelplacement === "left" && props.label ? "8px" : 0)};
  color: ${props => (props.disabled ? props.theme.gray2 : props.theme.gray6)};
  background-color: ${props => (props.disabled ? props.theme.gray1 : "transparent")};
  pointer-events: ${props => (props.disabled ? "none" : "all")};

`

const CheckedCheckboxIcon = styled(CheckboxIcon)`
  background-color: transparent;
  margin-right: ${props => (props.labelplacement === "right" && props.label ? "8px" : 0)};
  margin-left: ${props => (props.labelplacement === "left" && props.label ? "8px" : 0)};
  pointer-events: ${props => (props.disabled ? "none" : "all")};
  color: ${props => (props.disabled ? props.theme.gray2 : props.theme.primary)};
`

const Label = styled.span`
  cursor: pointer;
  color: ${props => props.theme.gray7};
`

const StyledTooltip = styled(ReactTooltip)`
  max-width: 30%;
  padding: 6px 10px;
  font-size: ${props => props.theme.textSm};

  &:before, &:after {
    border-width: 0px !important;
  }
`

const Checkbox = ({
  className = "",
  onChange = () => {},
  darkMode = false,
  value,
  label = "",
  labelPlacement,
  disabled = false,
  description,
  tooltipText,
  tooltipOptions,
}) => {
  const [randomId] = useState(randomString(10))

  return (
    <Box pt={1} pm={1}>
      <Container
        darkMode={darkMode}
        disabled={disabled}
        className={className}
      >
        {labelPlacement === "left" && label ?
          <Label
            className="label"
            onClick={() => {
              if (!disabled) onChange(!value)
            }}
          >
            {label}
            {tooltipText ?
              <Fragment>
                <FontAwesomeIcon
                  sx={{ paddingLeft: "4px", color: "grays.gray6" }}
                  icon={faQuestionCircle}
                  className="tooltip-icon"
                  data-tip={tooltipText}
                  data-for={randomId}
                />
                <StyledTooltip
                  id={randomId}
                  effect="solid"
                  offset={{ top: -5 }}
                  {...tooltipOptions}
                />
              </Fragment>
              :
              null
            }
          </Label>
          :
          null
        }
        {value ?
          <CheckedCheckboxIcon
            className="icon"
            icon={faCheckSquare}
            labelplacement={labelPlacement}
            label={label}
            disabled={disabled}
            onClick={() => {
              if (!disabled) onChange(false)
            }}
          />
          :
          <CheckboxIcon
            className="icon"
            labelplacement={labelPlacement}
            label={label}
            // icon={["far", "square"]}
            icon={faSquare}
            disabled={disabled}
            onClick={() => {
              if (!disabled) onChange(true)
            }}
          />
        }
        {labelPlacement === "right" && label ?
          <Label
            className="label"
            onClick={() => {
              if (!disabled) onChange(!value)
            }}
          >
            {label}
            {tooltipText ?
              <Fragment>
                <FontAwesomeIcon
                  sx={{ paddingLeft: "4px", color: "grays.gray6" }}
                  icon={faQuestionCircle}
                  className="tooltip-icon"
                  data-tip={tooltipText}
                  data-for={randomId}
                />
                <StyledTooltip
                  id={randomId}
                  effect="solid"
                  {...tooltipOptions}
                />
              </Fragment>
              :
              null
            }
          </Label>
          :
          null
        }
      </Container>
      {description && (
        <Text pt={1} sx={{ fontSize: 0, color: "grays.gray6", paddingLeft: "20px", lineHeight: "paragraph", whiteSpace: "pre-wrap" }}>{description}</Text>
      )}
    </Box>
  )
}

Checkbox.propTypes = propTypes
Checkbox.defaultProps = defaultProps

export default Checkbox
